import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/combineReducer";

const WIDGET_SRC =
  "https://atendoo.s3.us-east-1.amazonaws.com/widget/atendoo-widget.iife.js";

const ATENDIUM_WIDGET_TOKEN = process.env.REACT_APP_ATENDIUM_WIDGET_TOKEN || "";

const WIDGET_CONFIG = {
  serverUrl: "wss://ws.atendium.com/chat",
  token: ATENDIUM_WIDGET_TOKEN,
  agent: "af24f0ae-11c6-4248-a72b-fac618dd65f3",
};

type AtendooContext = {
  empresa: string;
  cuit: string;
  username?: string;
};

type AtendooWidgetApi = {
  init: (config: {
    serverUrl: string;
    token: string;
    agent: string;
    context: AtendooContext;
  }) => void;
  updateContext?: (context: AtendooContext) => void;
};

declare global {
  interface Window {
    AtendooWidget?: AtendooWidgetApi;
  }
}

type CompanyContextSource = {
  socialReason?: string;
  cuit?: string | number;
} | null;

let widgetReady = false;
let scriptRequested = false;

const buildContext = (company?: CompanyContextSource, username?: string): AtendooContext => ({
  empresa: company?.socialReason || "",
  username: username || "",
  cuit:
    company?.cuit != null && String(company.cuit) !== ""
      ? String(company.cuit)
      : "",
});

export default function AtendooWidget() {
  const token = useSelector((state: RootState) => state.auth.token);
  const currentCompany = useSelector(
    (state: RootState) =>
      state.user.currentCompany || state.user.companies?.[0] || null,
  );
  const username = useSelector((state: RootState) => state.user.username);
  const context = buildContext(currentCompany, username || "");
  const contextRef = useRef<AtendooContext>(context);
  contextRef.current = context;
  const hasCompanyContext = Boolean(context.empresa || context.cuit);

  useEffect(() => {
    if (!token || !ATENDIUM_WIDGET_TOKEN || !hasCompanyContext) return;

    let cancelled = false;
    let intervalId: number | undefined;

    const syncContext = () => {
      if (cancelled || !window.AtendooWidget) return;

      const context = contextRef.current;
      if (!widgetReady) {
        widgetReady = true;
        window.AtendooWidget.init({
          ...WIDGET_CONFIG,
          context,
        });
        return;
      }

      window.AtendooWidget.updateContext?.(context);
    };

    const waitForWidget = () => {
      intervalId = window.setInterval(() => {
        if (!window.AtendooWidget) return;
        if (intervalId) window.clearInterval(intervalId);
        syncContext();
      }, 200);
    };

    if (window.AtendooWidget) {
      syncContext();
    } else if (scriptRequested) {
      waitForWidget();
    } else {
      scriptRequested = true;
      const script = document.createElement("script");
      script.src = WIDGET_SRC;
      script.async = true;
      script.onload = () => syncContext();
      document.body.appendChild(script);
    }

    return () => {
      cancelled = true;
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [token, hasCompanyContext, context.empresa, context.cuit]);

  useEffect(() => {
    return () => {
      widgetReady = false;
      document.getElementById("atendoo-widget")?.replaceChildren();
    };
  }, []);

  if (!token || !ATENDIUM_WIDGET_TOKEN) return null;

  return <div id="atendoo-widget" />;
}
